import React from 'react';

import { Box, Grid, Popover, Theme, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TimeWindow } from '@uplift/services';
import { Button, useToggle } from '@uplift/ui-v2';

import { MonthRangePicker } from './MonthRangePicker.component';

export type MonthRangeFilterProps = {
  value: TimeWindow;
  onChange: (range: TimeWindow) => void;
  availableRange?: TimeWindow;
  className?: string;
  disabled?: boolean;
};


const useMonthRangeFilterStyles = makeStyles()({
  button: {
    textTransform: 'none',
  }
});


export const MonthRangeFilter: React.FC<MonthRangeFilterProps> = ({ value, availableRange, onChange, className, disabled }) => {
  const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
  const { classes, cx } = useMonthRangeFilterStyles();
  const [ anchorEl, setAnchorEl ] = React.useState<HTMLElement>();
  const [ open, handleOpen, handleClose ] = useToggle(false);

  const title = React.useMemo(() => {
    if (value.start) {
      const { start: first, end: second } = value;
      const isSameYear = first.isSame(second, 'year');

      const monthFormat = isSmall ? 'MMM' : 'MMMM';
      const yearFormat = isSmall ? 'YY' : 'YYYY';

      const firstDateFormat = isSameYear ? monthFormat : `${monthFormat}, ${yearFormat}`;
      const firstDateFormatted = first.format(firstDateFormat);

      const secondDateFormat = `${monthFormat}, ${yearFormat}`;
      const secondDateFormatted = second.format(secondDateFormat);

      return [ firstDateFormatted, secondDateFormatted ].join(' – ');
    }

    return undefined;
  }, [ isSmall, value ]);

  const minDate = availableRange.start || value.start;
  const maxDate = availableRange.end || value.end;

  return (
    <Box display="flex" alignItems="center">
      <Button
        className={cx(classes.button, className)}
        ref={setAnchorEl}
        onClick={handleOpen}
        startIcon={<FilterListIcon color={disabled ? 'disabled' : 'action'} />}
        variant="outlined"
        size={isSmall ? 'small' : 'medium'}
        disabled={disabled}
      >
        {title}
      </Button>
      <Popover
        open={open}
        keepMounted
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box p={2} maxWidth="320px">
          {minDate && maxDate && value.start && (
            <Grid container spacing={2}>
              <Grid size={12}>
                Filter by dates
              </Grid>
              <Grid size={12}>
                <MonthRangePicker labelFrom="From" labelTo="To" value={value} onChange={onChange} minDate={minDate} maxDate={maxDate} />
              </Grid>
            </Grid>
          )}
        </Box>
      </Popover>
    </Box>
  );
};
